import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Save, Check, Plus, Trash2, ChevronUp, ChevronDown, Copy, RefreshCw,
  Eye, EyeOff, Link2, AlertTriangle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Design tokens (mirror of the editor's house style — G/S are not exported there) ──
const G = {
  gold:      '#D4AF37',
  goldSec:   '#C6A75A',
  goldTer:   '#B8902A',
  dark:      '#1E1A14',
  muted:     '#7A6A40',
  cream:     '#FFFCF0',
  cinzel:    "'Cinzel', serif",
  cormorant: "'Cormorant Garamond', serif",
};

const S = {
  label: {
    display: 'block', fontFamily: G.cinzel, fontSize: 11, color: G.dark,
    marginBottom: 6, fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase',
  },
  input: {
    background: 'rgba(255,252,240,0.4)', border: '1px solid rgba(122,106,64,0.3)',
    color: G.dark, padding: '10px 14px', borderRadius: 8, fontFamily: G.cormorant,
    fontSize: 16, width: '100%', outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  btnPrimary: {
    background: `linear-gradient(135deg, ${G.gold} 0%, ${G.goldTer} 100%)`,
    color: G.dark, border: 'none', borderRadius: 8, padding: '10px 20px',
    fontFamily: G.cinzel, fontWeight: 700, fontSize: 12, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 6,
    boxShadow: '0 4px 12px rgba(212,175,55,0.3)', letterSpacing: '0.5px',
  },
  btnSecondary: {
    background: 'transparent', color: G.goldTer, border: `1px solid ${G.goldTer}`,
    borderRadius: 8, padding: '8px 16px', fontFamily: G.cinzel, fontWeight: 700,
    fontSize: 11, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
  },
  btnGhost: {
    background: 'transparent', color: G.muted, border: 'none', borderRadius: 6,
    padding: '6px 8px', fontFamily: G.cormorant, fontSize: 14, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 4,
  },
  hint: {
    fontFamily: G.cormorant, fontSize: 13, fontStyle: 'italic', color: G.muted,
    margin: '6px 0 0',
  },
  sectionTitle: {
    fontFamily: G.cinzel, fontSize: 14, color: G.dark, margin: '0 0 4px',
    fontWeight: 700, letterSpacing: '0.5px',
  },
  sectionSub: {
    fontFamily: G.cormorant, fontSize: 14, color: G.muted, margin: '0 0 16px',
  },
};

// Guest-site section catalogue — keys match the celebration config service.
const SECTION_LABELS = {
  welcome: 'Welcome',
  plan:    'Plan Your Celebration Day',
  latest:  'Latest from The Valenciano Family',
  help:    'How Can We Help?',
  message: 'Share a Message',
  nyc:     "The Valenciano Family's Guide to New York",
};

const EMPTY_REEL = { url: '', title: '', category: '', featured: false, thumbnail: '' };

const DEFAULT_CONFIG = {
  arrival_time: '', begin_time: '', parking_copy: '',
  attire_general: '', attire_men: '', attire_women: '',
  latest_update: '', latest_update_title: '',
  nyc_guide: { intro: '', reels: [] },
  sections: Object.keys(SECTION_LABELS).map(key => ({ key, visible: true })),
  oscar_prompts: [],
};

function normalizeConfig(raw) {
  const cfg = { ...DEFAULT_CONFIG, ...(raw || {}) };
  cfg.nyc_guide = {
    intro: raw?.nyc_guide?.intro || '',
    reels: Array.isArray(raw?.nyc_guide?.reels)
      ? raw.nyc_guide.reels.map(r => ({ ...EMPTY_REEL, ...r }))
      : [],
  };
  const known = Object.keys(SECTION_LABELS);
  const listed = Array.isArray(raw?.sections)
    ? raw.sections.filter(s => known.includes(s?.key)).map(s => ({ key: s.key, visible: s.visible !== false }))
    : [];
  const listedKeys = listed.map(s => s.key);
  known.forEach(key => { if (!listedKeys.includes(key)) listed.push({ key, visible: true }); });
  cfg.sections = listed;
  cfg.oscar_prompts = Array.isArray(raw?.oscar_prompts) ? raw.oscar_prompts.map(String) : [];
  return cfg;
}

// ── Small building blocks ──
function SectionCard({ title, subtitle, children }) {
  return (
    <div style={{
      border: '1px solid rgba(122,106,64,0.2)', borderRadius: 12,
      padding: 20, marginBottom: 20, background: 'rgba(255,252,240,0.25)',
    }}>
      <h4 style={S.sectionTitle}>{title}</h4>
      {subtitle && <p style={S.sectionSub}>{subtitle}</p>}
      {children}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={S.label}>{label}</label>
      <input
        data-testid={testId}
        style={S.input}
        value={value || ''}
        placeholder={placeholder || ''}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  );
}

function TextAreaField({ label, value, onChange, placeholder, hint, rows = 4, testId }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={S.label}>{label}</label>
      <textarea
        data-testid={testId}
        style={{ ...S.input, resize: 'vertical', minHeight: rows * 22 }}
        rows={rows}
        value={value || ''}
        placeholder={placeholder || ''}
        onChange={e => onChange(e.target.value)}
      />
      {hint && <p style={S.hint}>{hint}</p>}
    </div>
  );
}

function MoveButtons({ index, total, onMove }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <button
        type="button" style={{ ...S.btnGhost, padding: '2px 4px', opacity: index === 0 ? 0.3 : 1 }}
        disabled={index === 0} title="Move up"
        onClick={() => onMove(index, index - 1)}
      >
        <ChevronUp size={14} />
      </button>
      <button
        type="button" style={{ ...S.btnGhost, padding: '2px 4px', opacity: index === total - 1 ? 0.3 : 1 }}
        disabled={index === total - 1} title="Move down"
        onClick={() => onMove(index, index + 1)}
      >
        <ChevronDown size={14} />
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CELEBRATION EDITOR — self-contained tab: own load, own state, own Save.
// Saves through the celebration config service; template saving is untouched.
// ═══════════════════════════════════════════════════════════════════════════
export default function CelebrationEditor() {
  const [config, setConfig] = useState(null);
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  const [parties, setParties] = useState([]);
  const [ensuring, setEnsuring] = useState(false);
  const [copiedToken, setCopiedToken] = useState('');
  const [previewKey, setPreviewKey] = useState(0);

  const loadConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/celebration/config`);
      if (!res.ok) throw new Error('load failed');
      const data = await res.json();
      const cfg = normalizeConfig(data);
      setConfig(cfg);
      setSavedSnapshot(JSON.stringify(cfg));
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    }
    setLoading(false);
  }, []);

  const loadParties = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/parties`);
      if (!res.ok) throw new Error('load failed');
      const data = await res.json();
      setParties(Array.isArray(data) ? data : []);
    } catch {}
  }, []);

  useEffect(() => { loadConfig(); loadParties(); }, [loadConfig, loadParties]);

  const dirty = config !== null && JSON.stringify(config) !== savedSnapshot;

  const update = (patch) => {
    setConfig(prev => ({ ...prev, ...patch }));
    setSaved(false);
    setSaveFailed(false);
  };

  const handleSave = async () => {
    if (!config || saving) return;
    setSaving(true);
    setSaved(false);
    setSaveFailed(false);
    try {
      const res = await fetch(`${API}/api/celebration/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          arrival_time: config.arrival_time,
          begin_time: config.begin_time,
          parking_copy: config.parking_copy,
          attire_general: config.attire_general,
          attire_men: config.attire_men,
          attire_women: config.attire_women,
          latest_update: config.latest_update,
          latest_update_title: config.latest_update_title,
          nyc_guide: config.nyc_guide,
          sections: config.sections,
          oscar_prompts: config.oscar_prompts,
        }),
      });
      if (!res.ok) throw new Error('save failed');
      setSavedSnapshot(JSON.stringify(config));
      setSaved(true);
      setPreviewKey(k => k + 1); // show the fresh content in the preview
      setTimeout(() => setSaved(false), 2500);
    } catch {
      setSaveFailed(true);
    }
    setSaving(false);
  };

  // ── Reels ──
  const setReels = (reels) => update({ nyc_guide: { ...config.nyc_guide, reels } });

  const updateReel = (index, patch) => {
    const reels = config.nyc_guide.reels.map((r, i) => {
      if (i === index) return { ...r, ...patch };
      // Only one featured reel at a time — checking one unchecks the rest.
      if (patch.featured === true) return { ...r, featured: false };
      return r;
    });
    setReels(reels);
  };

  const moveReel = (from, to) => {
    const reels = [...config.nyc_guide.reels];
    const [item] = reels.splice(from, 1);
    reels.splice(to, 0, item);
    setReels(reels);
  };

  // ── Sections ──
  const moveSection = (from, to) => {
    const sections = [...config.sections];
    const [item] = sections.splice(from, 1);
    sections.splice(to, 0, item);
    update({ sections });
  };

  const toggleSection = (key) => {
    update({
      sections: config.sections.map(s => s.key === key ? { ...s, visible: !s.visible } : s),
    });
  };

  // ── Oscar prompts ──
  const setPrompts = (oscar_prompts) => update({ oscar_prompts });

  // ── Celebration links ──
  const withToken = useMemo(() => parties.filter(p => p.celebration_token), [parties]);
  const withoutToken = parties.length - withToken.length;
  const firstToken = withToken[0]?.celebration_token || '';

  const handleEnsureTokens = async () => {
    setEnsuring(true);
    try {
      const res = await fetch(`${API}/api/celebration/tokens/ensure`, { method: 'POST' });
      if (!res.ok) throw new Error('ensure failed');
      await loadParties();
    } catch {}
    setEnsuring(false);
  };

  const handleCopyLink = async (token) => {
    const link = `${window.location.origin}/celebration/${token}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(''), 2000);
    } catch {}
  };

  if (loading) {
    return (
      <div data-testid="celebration-editor" style={{ padding: 30, textAlign: 'center' }}>
        <p style={{ fontFamily: G.cormorant, fontSize: 16, color: G.muted }}>
          Loading celebration settings...
        </p>
      </div>
    );
  }

  if (loadFailed || !config) {
    return (
      <div data-testid="celebration-editor" style={{ padding: 30, textAlign: 'center' }}>
        <AlertTriangle size={20} color={G.goldTer} />
        <p style={{ fontFamily: G.cormorant, fontSize: 16, color: G.muted, marginTop: 10 }}>
          We couldn't load the celebration settings. Please try again in a moment.
        </p>
        <button type="button" style={{ ...S.btnSecondary, marginTop: 10 }} onClick={() => { setLoading(true); loadConfig(); }}>
          <RefreshCw size={12} /> Try Again
        </button>
      </div>
    );
  }

  const welcomeHidden = config.sections.some(s => s.key === 'welcome' && !s.visible);

  return (
    <div data-testid="celebration-editor">
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontFamily: G.cinzel, fontSize: 16, color: G.dark, margin: 0, fontWeight: 700 }}>
          Celebration Page
        </h3>
        <p style={{ fontFamily: G.cormorant, fontSize: 14, color: G.muted, margin: '4px 0 0' }}>
          Everything guests see on their private celebration page. Changes go live when you save.
        </p>
      </div>

      {/* a. Celebration Day */}
      <SectionCard title="Celebration Day" subtitle="Timing and arrival details for the day itself.">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label="Arrival Time" value={config.arrival_time} placeholder="11:45 AM"
            testId="cel-arrival-time" onChange={v => update({ arrival_time: v })} />
          <Field label="Celebration Begins" value={config.begin_time} placeholder="12:30 PM"
            testId="cel-begin-time" onChange={v => update({ begin_time: v })} />
        </div>
        <TextAreaField label="Parking & Arrival" value={config.parking_copy} rows={3}
          testId="cel-parking-copy"
          hint="Shown when guests tap Parking & Arrival."
          onChange={v => update({ parking_copy: v })} />
      </SectionCard>

      {/* b. What to Wear */}
      <SectionCard title="What to Wear" subtitle="Attire guidance shown to every guest.">
        <Field label="Overall Attire" value={config.attire_general}
          testId="cel-attire-general" onChange={v => update({ attire_general: v })} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label="For Men" value={config.attire_men}
            testId="cel-attire-men" onChange={v => update({ attire_men: v })} />
          <Field label="For Women" value={config.attire_women}
            testId="cel-attire-women" onChange={v => update({ attire_women: v })} />
        </div>
      </SectionCard>

      {/* c. Latest Note */}
      <SectionCard title="Latest Note" subtitle="A personal note from the family, front and center on the guest page.">
        <Field label="Note Title" value={config.latest_update_title}
          testId="cel-latest-title" onChange={v => update({ latest_update_title: v })} />
        <TextAreaField label="Note" value={config.latest_update} rows={4}
          testId="cel-latest-update"
          hint={'When this is empty, guests see "We’re looking forward to celebrating with you."'}
          onChange={v => update({ latest_update: v })} />
      </SectionCard>

      {/* d. The Valenciano Family's Guide to New York */}
      <SectionCard title="The Valenciano Family's Guide to New York" subtitle="Your personal city guide — each Reel becomes a card guests can tap.">
        <Field label="Guide Introduction" value={config.nyc_guide.intro}
          testId="cel-nyc-intro"
          onChange={v => update({ nyc_guide: { ...config.nyc_guide, intro: v } })} />

        {config.nyc_guide.reels.length === 0 && (
          <p style={{ ...S.hint, margin: '4px 0 12px' }}>
            Paste your Facebook Reel links here — each becomes a card on the guest site.
          </p>
        )}

        {config.nyc_guide.reels.map((reel, i) => (
          <div key={i} data-testid={`cel-reel-row-${i}`} style={{
            display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 12,
            padding: 12, borderRadius: 10, border: '1px solid rgba(122,106,64,0.2)',
            background: 'rgba(255,252,240,0.35)',
          }}>
            <MoveButtons index={i} total={config.nyc_guide.reels.length} onMove={moveReel} />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 8 }}>
                <input style={S.input} placeholder="Reel link" value={reel.url}
                  onChange={e => updateReel(i, { url: e.target.value })} />
                <input style={S.input} placeholder="Title" value={reel.title}
                  onChange={e => updateReel(i, { title: e.target.value })} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginBottom: 8 }}>
                <input style={S.input} placeholder="Category (e.g. Food)" value={reel.category}
                  onChange={e => updateReel(i, { category: e.target.value })} />
                <input style={S.input} placeholder="Thumbnail image link (optional)" value={reel.thumbnail}
                  onChange={e => updateReel(i, { thumbnail: e.target.value })} />
              </div>
              <label style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontFamily: G.cormorant, fontSize: 14, color: G.dark, cursor: 'pointer',
              }}>
                <input type="checkbox" checked={!!reel.featured}
                  onChange={e => updateReel(i, { featured: e.target.checked })} />
                Featured (shown first — only one at a time)
              </label>
            </div>
            <button type="button" style={{ ...S.btnGhost, color: '#9E4444' }} title="Remove this Reel"
              onClick={() => setReels(config.nyc_guide.reels.filter((_, j) => j !== i))}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}

        <button type="button" style={S.btnSecondary} data-testid="cel-add-reel"
          onClick={() => setReels([...config.nyc_guide.reels, { ...EMPTY_REEL }])}>
          <Plus size={12} /> Add a Reel
        </button>
      </SectionCard>

      {/* e. Sections */}
      <SectionCard title="Sections" subtitle="Choose which sections guests see, and in what order (top to bottom).">
        {config.sections.map((section, i) => (
          <div key={section.key} data-testid={`cel-section-row-${section.key}`} style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
            borderRadius: 10, border: '1px solid rgba(122,106,64,0.2)', marginBottom: 8,
            background: section.visible ? 'rgba(255,252,240,0.35)' : 'rgba(122,106,64,0.08)',
          }}>
            <MoveButtons index={i} total={config.sections.length} onMove={moveSection} />
            <span style={{
              flex: 1, fontFamily: G.cormorant, fontSize: 16,
              color: section.visible ? G.dark : G.muted,
            }}>
              {SECTION_LABELS[section.key] || section.key}
            </span>
            <button type="button" style={{ ...S.btnGhost, color: section.visible ? G.goldTer : G.muted }}
              title={section.visible ? 'Shown to guests — tap to hide' : 'Hidden from guests — tap to show'}
              onClick={() => toggleSection(section.key)}>
              {section.visible ? <Eye size={15} /> : <EyeOff size={15} />}
              <span style={{ fontFamily: G.cinzel, fontSize: 10, letterSpacing: '0.5px' }}>
                {section.visible ? 'Shown' : 'Hidden'}
              </span>
            </button>
          </div>
        ))}
        {welcomeHidden && (
          <p style={{ ...S.hint, color: G.goldTer }}>
            <AlertTriangle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            The Welcome section is hidden — guests will land without a greeting. We recommend keeping it on.
          </p>
        )}
      </SectionCard>

      {/* f. Oscar prompt suggestions */}
      <SectionCard title="Oscar Prompt Suggestions" subtitle="Short tap-to-ask suggestions guests see in the Oscar chat.">
        {config.oscar_prompts.map((prompt, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <input style={S.input} value={prompt} placeholder="e.g. What time should I arrive?"
              onChange={e => setPrompts(config.oscar_prompts.map((p, j) => j === i ? e.target.value : p))} />
            <button type="button" style={{ ...S.btnGhost, color: '#9E4444' }} title="Remove this suggestion"
              onClick={() => setPrompts(config.oscar_prompts.filter((_, j) => j !== i))}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button type="button" style={S.btnSecondary} data-testid="cel-add-prompt"
          onClick={() => setPrompts([...config.oscar_prompts, ''])}>
          <Plus size={12} /> Add a Suggestion
        </button>
      </SectionCard>

      {/* g. Celebration links */}
      <SectionCard title="Celebration Links" subtitle="Each party gets its own private link to the celebration page.">
        <p style={{ fontFamily: G.cormorant, fontSize: 15, color: G.dark, margin: '0 0 10px' }}>
          {withToken.length} of {parties.length} parties have their celebration link.
          {withoutToken > 0 && ` ${withoutToken} still need one.`}
        </p>
        {withoutToken > 0 && (
          <button type="button" data-testid="cel-ensure-tokens" style={S.btnPrimary}
            disabled={ensuring} onClick={handleEnsureTokens}>
            <Link2 size={12} /> {ensuring ? 'Creating links...' : 'Create missing links'}
          </button>
        )}
        {withToken.slice(0, 5).map(p => (
          <div key={p.celebration_token} style={{
            display: 'flex', alignItems: 'center', gap: 10, marginTop: 10,
            padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(122,106,64,0.2)',
            background: 'rgba(255,252,240,0.35)',
          }}>
            <span style={{ fontFamily: G.cormorant, fontSize: 15, color: G.dark, flex: '0 0 auto' }}>
              {p.party_name}
            </span>
            <span style={{
              fontFamily: G.cormorant, fontSize: 13, color: G.muted, flex: 1,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', direction: 'rtl',
            }}>
              {`/celebration/${p.celebration_token}`}
            </span>
            <button type="button" style={S.btnGhost} title="Copy this party's private link"
              onClick={() => handleCopyLink(p.celebration_token)}>
              {copiedToken === p.celebration_token ? <Check size={13} color="#1E7344" /> : <Copy size={13} />}
              <span style={{ fontFamily: G.cinzel, fontSize: 10 }}>
                {copiedToken === p.celebration_token ? 'Copied' : 'Copy'}
              </span>
            </button>
          </div>
        ))}
        {withToken.length > 5 && (
          <p style={S.hint}>Showing the first 5 of {withToken.length} links. Every party's link is available from their profile.</p>
        )}
        <p style={S.hint}>
          These links are private — each one opens the page personalized for that party. Share them only with the party they belong to.
        </p>
      </SectionCard>

      {/* Save bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12,
        paddingTop: 16, borderTop: '1px solid rgba(122,106,64,0.2)', marginBottom: 28,
      }}>
        {saveFailed && (
          <span style={{ fontFamily: G.cormorant, fontSize: 14, color: '#9E4444' }}>
            Something went wrong while saving. Please try again.
          </span>
        )}
        {dirty && !saving && !saveFailed && (
          <span style={{ fontFamily: G.cormorant, fontSize: 14, color: G.goldTer, fontStyle: 'italic' }}>
            Unsaved changes
          </span>
        )}
        {saved && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontFamily: G.cinzel, fontSize: 11, color: '#1E7344', fontWeight: 700,
          }}>
            <Check size={13} /> Saved
          </span>
        )}
        <button
          type="button"
          data-testid="cel-save-config"
          style={{ ...S.btnPrimary, opacity: dirty && !saving ? 1 : 0.55, cursor: dirty && !saving ? 'pointer' : 'default' }}
          disabled={!dirty || saving}
          onClick={handleSave}
        >
          <Save size={12} /> {saving ? 'Saving...' : 'Save Celebration Page'}
        </button>
      </div>

      {/* Mobile preview */}
      <SectionCard
        title="Guest Preview"
        subtitle="A live look at the celebration page, exactly as a guest sees it on their phone."
      >
        {firstToken ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
              <button type="button" style={S.btnSecondary} data-testid="cel-preview-refresh"
                onClick={() => setPreviewKey(k => k + 1)}>
                <RefreshCw size={12} /> Refresh Preview
              </button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: 312, height: 576, overflow: 'hidden' }}>
                <iframe
                  key={previewKey}
                  data-testid="cel-preview-iframe"
                  title="Celebration page preview"
                  src={`/celebration/${firstToken}`}
                  style={{
                    width: 390, height: 720, border: '1px solid rgba(122,106,64,0.3)',
                    borderRadius: 24, transform: 'scale(0.8)', transformOrigin: 'top left',
                    background: '#000',
                  }}
                />
              </div>
            </div>
            <p style={{ ...S.hint, textAlign: 'center' }}>
              Previewing as {withToken[0]?.party_name || 'the first party'}.
            </p>
          </div>
        ) : (
          <p style={S.hint}>
            The preview appears once celebration links exist — use "Create missing links" above to set them up.
          </p>
        )}
      </SectionCard>
    </div>
  );
}
